import { useTranslation } from 'react-i18next';
import { useForm } from 'react-hook-form';
import { Button, Flex, Modal, Stack } from '@mantine/core';

import { usePatchOrganisation } from 'api/organisations';
import { Form, TextField } from 'components/shared';
import { useShowNotification } from 'hooks';
import type { OrganisationType } from 'types';

type Props = {
  opened: boolean;
  onClose: () => void;
  organisation: OrganisationType;
};

export const CompanyDataSettingsModal = ({
  opened,
  onClose,
  organisation,
}: Props) => {
  const { t } = useTranslation('organisations');
  const form = useForm({
    defaultValues: {
      name: organisation.details.name,
      street: organisation.details.street,
      postalCode: organisation.details.postalCode,
      city: organisation.details.city,
      website: organisation.details.website,
    },
  });
  const { mutateAsync: patchOrganisation } = usePatchOrganisation({
    id: organisation.id,
  });
  const showNotification = useShowNotification();

  const onSubmit = async (data: FormData) => {
    const details = Object.entries(data).reduce(
      (acc, [key, value]) => {
        acc[key as keyof OrganisationType['details']] = value || null;
        return acc;
      },
      {} as Partial<OrganisationType['details']>,
    );
    try {
      await patchOrganisation({ details });
      showNotification({
        message: t(
          'organisationView.details.companyData.modal.updateSuccessMessage',
        ),
        variant: 'success',
      });
      onClose();
    } catch (error) {
      showNotification({
        message: t(
          'organisationView.details.companyData.modal.updateErrorMessage',
        ),
        variant: 'error',
      });
    }
  };

  return (
    <Modal
      opened={opened}
      onClose={onClose}
      title={t('organisationView.details.companyData.modal.title')}
      centered
      size="md"
    >
      <Form form={form} onSubmit={onSubmit}>
        <Stack
          sx={theme => ({
            marginBottom: theme.other.spacing(3),
          })}
        >
          <TextField
            name="name"
            label={t('organisationView.details.companyData.fields.name')}
            withErrorWrapperAlwaysEnabled={false}
          />
          <TextField
            name="street"
            label={t('organisationView.details.companyData.fields.street')}
            withErrorWrapperAlwaysEnabled={false}
          />
          <TextField
            name="postalCode"
            label={t('organisationView.details.companyData.fields.postalCode')}
            withErrorWrapperAlwaysEnabled={false}
          />
          <TextField
            name="city"
            label={t('organisationView.details.companyData.fields.city')}
            withErrorWrapperAlwaysEnabled={false}
          />
          <TextField
            name="website"
            label={t('organisationView.details.companyData.fields.website')}
            withErrorWrapperAlwaysEnabled={false}
          />
        </Stack>
        <Flex justify="flex-end" gap="lg">
          <Button variant="default" onClick={onClose}>
            {t('organisationView.details.companyData.modal.cancelButton')}
          </Button>
          <Button loading={form.formState.isSubmitting} type="submit">
            {t('organisationView.details.companyData.modal.saveButton')}
          </Button>
        </Flex>
      </Form>
    </Modal>
  );
};
